import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import rotationsServiceApi, {
  type CreateRotationPayload,
  type KnowledgeRef,
  type Rotation,
  type RotationTimeslot,
} from '../api/rotations.service.api';
import { type QueryOpts } from '../api/query-client';
import { type EditableTeamGroup } from '../constants/rotations.constants';
import { queryKeys } from '../api/query-client-registry';

/**
 * React-query hooks for the rotations domain. All consumers should reach the
 * rotations API through these hooks.
 *
 * Read hooks honor `AbortSignal` end-to-end (cancellation cancels the HTTP
 * request). Mutation hooks update the cache via `setQueryData` for fast
 * follow-on renders; only when the server is the sole source of truth do we
 * fall back to `invalidateQueries`.
 */

/**
 * Hierarchical query keys. Use the most specific key for individual reads,
 * and a parent key (e.g. `rotationsKeys.all`) to invalidate everything in
 * the domain.
 */
export const rotationsKeys = {
  // `all` and `trainees` derive from the registry so the prefixes stay identical
  // to the ones the user-management thunks invalidate (see `queryKeys` in
  // ../api/query-client-registry) — a drift here would silently break invalidation.
  all: queryKeys.rotationsAll,
  list: () => [...rotationsKeys.all, 'list'] as const,
  timeslots: (params: TimeslotsKeyParams) => [...rotationsKeys.all, 'timeslots', params] as const,
  detail: (id: string | null | undefined) => [...rotationsKeys.all, 'detail', id] as const,
  trainees: (id: string | null | undefined) => [...queryKeys.rotationsTrainees, id] as const,
  fromUser: () => [...rotationsKeys.all, 'fromUser'] as const,
};

interface TimeslotsKeyParams {
  userIds?: string[];
  start?: string;
  end?: string;
}

// ---------- Reads ----------

export function useAllRotations(options: QueryOpts<Rotation[]> = {}) {
  return useQuery<Rotation[]>({
    queryKey: rotationsKeys.list(),
    queryFn: ({ signal }) => rotationsServiceApi.getAllRotations({ signal }),
    ...options,
  });
}

export function useTimeslots(
  { userIds, start, end }: { userIds?: string[]; start: Date; end: Date },
  options: QueryOpts<RotationTimeslot[]> = {},
) {
  // Stable, serializable key — Date objects don't compare equal across renders.
  const keyParams: TimeslotsKeyParams = {
    userIds,
    start: start?.toISOString(),
    end: end?.toISOString(),
  };
  return useQuery<RotationTimeslot[]>({
    queryKey: rotationsKeys.timeslots(keyParams),
    queryFn: ({ signal }) => rotationsServiceApi.getTimeslots({ userIds, start, end, signal }),
    enabled: !!start && !!end,
    ...options,
  });
}

export function useRotation(id: string | null | undefined, options: QueryOpts<Rotation> = {}) {
  return useQuery<Rotation>({
    queryKey: rotationsKeys.detail(id),
    queryFn: ({ signal }) => rotationsServiceApi.getRotationById(id as string, { signal }),
    enabled: !!id,
    ...options,
  });
}

export function useTrainees(
  rotationId: string | null | undefined,
  options: QueryOpts<unknown[]> = {},
) {
  return useQuery<unknown[]>({
    queryKey: rotationsKeys.trainees(rotationId),
    queryFn: ({ signal }) => rotationsServiceApi.getTrainees(rotationId as string, { signal }),
    enabled: !!rotationId,
    ...options,
  });
}

export function useRotationFromUser(options: QueryOpts<Rotation> = {}) {
  return useQuery<Rotation>({
    queryKey: rotationsKeys.fromUser(),
    queryFn: ({ signal }) => rotationsServiceApi.getRotationFromUser({ signal }),
    ...options,
  });
}

// ---------- Mutations ----------

const setListField = (qc: QueryClient, id: string, patch: Partial<Rotation>) =>
  qc.setQueryData<Rotation[]>(rotationsKeys.list(), prev =>
    Array.isArray(prev) ? prev.map(r => (r.id === id ? { ...r, ...patch } : r)) : prev,
  );

const setDetailField = (qc: QueryClient, id: string, updater: (prev: Rotation) => Rotation) =>
  qc.setQueryData<Rotation>(rotationsKeys.detail(id), prev => (prev ? updater(prev) : prev));

export function useUpdateRotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rotationId,
      payload,
    }: {
      rotationId: string;
      payload: Record<string, unknown>;
    }) => rotationsServiceApi.updateRotation({ rotationId, payload }),
    onSuccess: (data, { rotationId, payload }) => {
      // Prefer the server's normalized response when available (e.g.,
      // uppercased codeAliases); fall back to the raw payload.
      const next = (data && typeof data === 'object' ? data : payload) as Partial<Rotation>;
      setDetailField(qc, rotationId, prev => ({ ...prev, ...next }));
      setListField(qc, rotationId, next);
    },
  });
}

export function useCreateRotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRotationPayload) => rotationsServiceApi.createRotation(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rotationsKeys.list() });
    },
  });
}

export function useCreateSchedule() {
  return useMutation({
    mutationFn: (payload: { name: string }) => rotationsServiceApi.createSchedule(payload),
  });
}

export function useDeleteRotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rotationId,
      payload,
    }: {
      rotationId: string;
      payload: { destinationRotationId?: string; cloneContent?: boolean };
    }) => rotationsServiceApi.deleteRotation({ rotationId, payload }),
    onSuccess: (_, { rotationId }) => {
      qc.invalidateQueries({ queryKey: rotationsKeys.list() });
      qc.removeQueries({ queryKey: rotationsKeys.detail(rotationId) });
    },
  });
}

export function useAddKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rotationId, payload }: { rotationId: string; payload: KnowledgeRef }) =>
      rotationsServiceApi.addKnowledge({ rotationId, payload }),
    onSuccess: (_, { rotationId, payload }) => {
      setDetailField(qc, rotationId, prev => ({
        ...prev,
        knowledge: [...(prev.knowledge ?? []), payload],
      }));
    },
  });
}

export function useRemoveKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rotationId, id }: { rotationId: string; id: string }) =>
      rotationsServiceApi.removeKnowledge({ rotationId, id }),
    onSuccess: (_, { rotationId, id }) => {
      setDetailField(qc, rotationId, prev => ({
        ...prev,
        knowledge: (prev.knowledge ?? []).filter(k => k.id !== id),
      }));
    },
  });
}

export function useUpdateWelcomeLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rotationId,
      payload,
    }: {
      rotationId: string;
      payload: { deliverDaysBefore: number | null; subject: string; body: string };
    }) => rotationsServiceApi.updateWelcomeLetter({ rotationId, payload }),
    onSuccess: (data, { rotationId, payload }) => {
      const welcomeLetter = data && typeof data === 'object' ? data : payload;
      setDetailField(qc, rotationId, prev => ({ ...prev, welcomeLetter }));
    },
  });
}

export function useUpdateRotationOverview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rotationId, payload }: { rotationId: string; payload: { overview: string } }) =>
      rotationsServiceApi.updateRotationOverview({ rotationId, payload }),
    onSuccess: (data, { rotationId, payload }) => {
      const overview = data && typeof data === 'object' ? data.overview : payload.overview;
      setDetailField(qc, rotationId, prev => ({ ...prev, overview }));
      setListField(qc, rotationId, { overview });
    },
  });
}

export function useSendPreviewWelcomeLetter() {
  return useMutation({
    mutationFn: ({
      rotationId,
      payload,
    }: {
      rotationId: string;
      payload: { subject: string; body: string };
    }) => rotationsServiceApi.sendPreviewWelcomeLetter({ rotationId, payload }),
  });
}

export function useAddMembersToTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rotationId,
      group,
      members,
    }: {
      rotationId: string;
      group: EditableTeamGroup;
      members: Array<{ id: string; type: string }>;
    }) => rotationsServiceApi.addMembersToTeam({ rotationId, group, payload: members }),
    onSuccess: (_, { rotationId, group, members }) => {
      setDetailField(qc, rotationId, prev => {
        const team = prev.team || {};
        return {
          ...prev,
          team: {
            ...team,
            [group]: [...(team[group] || []), ...members],
          },
        };
      });
    },
  });
}

export function useRemoveMemberFromTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rotationId,
      group,
      memberId,
    }: {
      rotationId: string;
      group: EditableTeamGroup;
      memberId: string;
    }) => rotationsServiceApi.removeMemberFromTeam({ rotationId, group, memberId }),
    onSuccess: (_, { rotationId, group, memberId }) => {
      setDetailField(qc, rotationId, prev => {
        const team = prev.team || {};
        return {
          ...prev,
          team: {
            ...team,
            [group]: (team[group] || []).filter(m => m.id !== memberId),
          },
        };
      });
    },
  });
}

export function useCreateRotationsTimeSlots() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      rotations: unknown[];
      skipEmail: boolean;
      createUserIfNotExits: boolean;
    }) => rotationsServiceApi.createRotationsTimeSlots(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...rotationsKeys.all, 'timeslots'] });
    },
  });
}
